import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import ImageUpload from "@/components/tournament/ImageUpload";

const SCHEMAS = {
  games: { label: "Game", fields: [["name", "Name"], ["publisher", "Publisher"], ["genre", "Genre"], ["description", "Short description"], ["tag", "Category"], ["color", "Accent color (hex)"], ["img", "Image", "image"], ["order", "Order", "number"]] },
  teams: { label: "Teams", fields: [["name", "Team name"], ["pts", "Points", "number"], ["rank", "Rank", "number"], ["logo", "Logo", "image"]] },
  matches: { label: "Matches", fields: [["a", "Team A"], ["b", "Team B"], ["sa", "Score A", "number"], ["sb", "Score B", "number"], ["time", "Time (e.g. 19:00)"], ["league", "League"], ["live", "Live", "bool"]] },
};

export default function AdminGames() {
  const [tab, setTab] = useState("games");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const schema = SCHEMAS[tab];
  const load = () => api.get(`/${tab}`).then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, [tab]);

  const openNew = () => { const f = {}; schema.fields.forEach(([k, , t]) => (f[k] = t === "bool" ? false : t === "number" ? 0 : "")); setForm(f); setEditing("new"); };
  const openEdit = (it) => { setForm({ ...it }); setEditing(it.id); };

  const save = async () => {
    const payload = {};
    schema.fields.forEach(([k, , t]) => { payload[k] = t === "number" ? Number(form[k]) || 0 : form[k]; });
    try {
      if (editing === "new") await api.post(`/admin/${tab}`, payload);
      else await api.put(`/admin/${tab}/${editing}`, payload);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this item?")) return; try { await api.delete(`/admin/${tab}/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } };

  const inp = "w-full px-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[hsl(var(--primary))]";

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-white">Live Content</h1>
          <p className="text-sm text-white/50">Manage games, team rankings, and the match schedule shown across the site.</p>
        </div>
        <button onClick={openNew} data-testid="content-new-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(var(--primary))] text-white text-sm font-bold"><Plus size={16} /> Add {schema.label === "Game" ? "Game" : schema.label.slice(0, -1)}</button>
      </div>

      <div className="flex gap-2 mb-4">
        {Object.entries(SCHEMAS).map(([k, v]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`content-tab-${k}`}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold ${tab === k ? "bg-white text-black" : "card-surface text-white/60 hover:text-white"}`}>{v.label}</button>
        ))}
      </div>

      <div className="card-surface rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-white/40 border-b border-white/10">
            {schema.fields.slice(0, 4).map(([k, l]) => <th key={k} className="p-3 font-semibold">{l}</th>)}
            <th className="p-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                {schema.fields.slice(0, 4).map(([k]) => <td key={k} className="p-3 text-white/80">{String(it[k])}</td>)}
                <td className="p-3"><div className="flex justify-end gap-1">
                  <button onClick={() => openEdit(it)} className="p-2 rounded-md text-white/50 hover:text-white hover:bg-white/5"><Pencil size={15} /></button>
                  <button onClick={() => remove(it.id)} className="p-2 rounded-md text-white/50 hover:text-red-400 hover:bg-white/5"><Trash2 size={15} /></button>
                </div></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-white/40">No items.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setEditing(null)} />
          <div className="relative w-full max-w-md card-surface rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
            <button onClick={() => setEditing(null)} className="absolute top-4 right-4 p-1.5 text-white/40 hover:text-white"><X size={18} /></button>
            <h2 className="text-lg font-bold text-white mb-4">{editing === "new" ? "Add" : "Edit"} {schema.label === "Game" ? "Game" : schema.label.slice(0, -1)}</h2>
            <div className="space-y-3">
              {schema.fields.map(([k, l, t]) => (
                <div key={k}>
                  {t === "image" ? (
                    <><label className="block text-xs font-semibold text-white/50 mb-1">{l}</label><ImageUpload value={form[k]} onChange={(v) => setForm((cur) => ({ ...cur, [k]: v }))} testid={`content-${k}-upload`} aspect="aspect-[4/3]" /></>
                  ) : t === "bool" ? (
                    <label className="flex items-center gap-2"><input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} className="w-4 h-4 accent-[hsl(var(--primary))]" /><span className="text-sm text-white/80">{l}</span></label>
                  ) : (
                    <>
                      <label className="block text-xs font-semibold text-white/50 mb-1">{l}</label>
                      <input type={t === "number" ? "number" : "text"} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inp} />
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setEditing(null)} className="flex-1 py-2.5 rounded-lg border border-white/15 text-white font-semibold">Cancel</button>
              <button onClick={save} data-testid="content-save" className="flex-1 py-2.5 rounded-lg bg-[hsl(var(--primary))] text-white font-bold">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
